import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {Alert, DSTFormOption, StatementTrackerService} from './statement-tracker.service';
import { HttpErrorResponse } from '@angular/common/http';
import {DSTStatement} from '../shared/models/dst-statement.model';
import {ModalComponent} from '@appkit4/angular-components/modal';
import {ProjectTaggingComponent} from '../shared/components/project-tagging/project-tagging.component';
import {DfMeDropdownComponent} from '../../shared/components/df-me-dropdown/df-me-dropdown.component';
import {SaveActionResponse} from '../../shared/_models/save-action-response.model';
import {CopyReplicateComponent} from './copy-replicate/copy-replicate.component';
import {TrackerTabsService, TrackerTabState} from './tracker-tabs.service';
import {DSTDynamicComponentResolverService, DSTSavePayload} from './create-disclosure/shared/dst-dynamic-component-resolver.service';
import {MatchTrackerComponent} from './match-tracker/match-tracker.component';
import {CreateOptionsComponent} from './create-options/create-options.component';
import {PtableColConfigModel} from '../shared/models/ptable-col-config.model';
import {MatSort} from '@angular/material/sort';
import {MatTable} from '@angular/material/table';
import {DstMatTableDataSource} from '../shared/mat-table/dst-mat-table-data-source';
import {DstFilterEvent, DstMatTableFilterApi} from '../shared/mat-table/dst-mat-table-filter-api';
import {refreshDstMatStickyColumns} from '../shared/mat-table/dst-mat-grid.util';
import {StmtBtnActionPayload} from '../shared/components/stmt-id-btn/stmt-id-btn.component';
import {StmtIdBtnService} from '../shared/components/stmt-id-btn/stmt-id-btn.service';
import {DSTSharedService, DSTTableActionStatus} from '../shared/dst-shared.service';
import {DSTMarkAsOption} from '../shared/components/stmt-id-btn/dst-mark-as-template/dst-mark-as-template.component';
import {JSONExportHeader, JSONExportService} from '../../shared/_services/JSONExport.service';
import {DSTChildFormState} from '../shared/models/dst-child-form-state.model';
import {DSTGridFilter, DSTGridFilterOption, DSTGridFilterVal} from '../shared/models/dst-grid-filter.model';
import {FormReivewPdf} from '../../shared/components/form-review-pdf/form-review-pdf.service';
import { CreateDisclosureSharedService } from './create-disclosure/shared/create-disclosure-shared.service';
import { MessageService } from '../shared/services/message.service';
import { MenuPosPayload } from '../shared/components/stmt-id-btn/smartMenuPos.directive';
import * as _ from 'lodash-es';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../shared/workspace-element-context';

@Component({
  selector: 'gtw-statement-tracker',
  templateUrl: './statement-tracker.component.html',
  styleUrls: ['./statement-tracker.component.scss', '../shared/mat-table/dst-mat-table.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StatementTrackerComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  // events dispatched and to be listened for in gtw-ui
  // @Output('createNew') showCreateDisclosure: EventEmitter<any>;
  @Output('show-data-loss-confirmation') showDataLossConfirmation: EventEmitter<any> = new EventEmitter<any>();
  @Output('is-child-statement-edit') isChildEditing: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('scrollSort') scrollMatSort!: MatSort;
  @ViewChild(MatTable) matTable!: MatTable<DSTStatement>;
  @ViewChild('matGridContainer') matGridContainer!: ElementRef<HTMLElement>;
  @ViewChild('dstDeleteConfirmDialog') dstDeleteConfirmDialog!: ModalComponent;
  @ViewChild('dstMarkOtherReasonDialog') dstMarkOtherReasonDialog!: ModalComponent;
  @ViewChild('dstUnmatchConfirmDialog') dstUnmatchConfirmDialog!: ModalComponent;
  @ViewChild('pdfViewerModal') pdfViewerModal!: ModalComponent;
  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;
  @ViewChild(ProjectTaggingComponent) projectTaggingComponent!: ProjectTaggingComponent;
  @ViewChild(CopyReplicateComponent) copyRepComponent!: CopyReplicateComponent;
  @ViewChild(MatchTrackerComponent) matchTrackerComponent!: MatchTrackerComponent;
  @ViewChild(CreateOptionsComponent) createOptionsComponent!: CreateOptionsComponent;

  taxYears: any[] = [
    { value: 0, label: '2017' },
    { value: 1, label: '2018' },
    { value: 2, label: '2019' },
    { value: 3, label: '2020', selected: true },
    { value: 4, label: '2021' }
  ];
  tabs: TrackerTabState[];
  activeTab!: TrackerTabState | null;
  createNewTabIsOpen!: boolean;
  tableData: DSTStatement[];
  tableDataFiltered: DSTStatement[];
  dataSource = new DstMatTableDataSource<DSTStatement>([]);
  table: DstMatTableFilterApi<DSTStatement>;
  displayedColumns: string[] = [];
  filterColumns: string[] = [];
  tableDataLoading: boolean = false;
  tableActionStatus: DSTTableActionStatus;
  filtersOptions: DSTGridFilter[];
  // Appkit's dropdown does not narrow its own list while searching; it emits the
  // matches and expects them to be bound back, so each column filter renders
  // from here rather than straight off filtersOptions.
  dropdownOptions: { [filterId: string]: DSTGridFilterOption[] } = {};
  private readonly noDropdownOptions: DSTGridFilterOption[] = [];
  cols: PtableColConfigModel[] = [
    {
      field: 'TAXYEAR',
      header: 'Tax Year',
      width: '80px'
    },
    {
      field: 'businessName',
      header: 'Business /ME',
      width: '240px'
      // searchIn: ['businessName', 'meName'],
    },
    {
      field: 'disclosureShortName',
      header: 'Disclosure',
      width: '140px'
    },
    {
      field: 'creationSource',
      header: 'Creation Source',
      width: '250px'
      // hidden: true
    },
    {
      field: 'attachment_name',
      header: 'File Name',
      width: '300px'
      // hidden: true
    },
    {
      field: 'gtw_source_desc',
      header: 'GTW Source',
      width: '250px'
    },
    {
      field: 'associatedStatements',
      header: 'Associated Disclosure(s)',
      width: '200px'
    },
    {
      field: 'projectName',
      header: 'Project',
      width: '180px'
      // searchIn: ['projectName', 'projectStepName'],
      // className: 'th-project'
    },
    {
      field: 'dcnName',
      header: 'Filing Group',
      width: '315px'
    },
    {
      field: 'createdBy',
      header: 'User Activity',
      width: '380px'
      // searchIn: ['createdBy', 'createdOnFormatted', 'modifiedBy', 'modifiedFormatted'],
    },
    {
      field: 'pdfPreview',
      header: 'PDF Preview',
      width: '130px'
    },
  ];
  frozenCols: PtableColConfigModel[] = [
    {
      field: 'statementId',
      header: 'Statement ID',
      width: '150px'
    },
    {
      field: 'leId',
      header: 'Legal Entity',
      width: '300px'
    },
  ];
  filters: DSTGridFilter[]= [
    {
      filterId: 'leid',
      filterType: 'list',
      filterValues: [],
      render:((row: DSTStatement)=>row.leId + ' - ' +row.entityName),
      searchProp:'leId'
    },
    {
      filterId: 'tax_year',
      filterType: 'list',
      filterValues: [],
      render:((row: DSTStatement)=>row.TAXYEAR),
      searchProp:'TAXYEAR'
    },
    {
      filterId: 'project',
      filterType: 'list',
      filterValues: [],
      render:((row: DSTStatement)=>row.projectName),
      searchProp:'projectName'
    },
    {
      filterId: 'disclosure',
      filterType: 'list',
      filterValues: [],
      render:((row: DSTStatement)=>row.disclosureShortName),
      searchProp:'disclosureShortName'
    },
    {
      filterId: 'creationSrc',
      filterType: 'list',
      filterValues: [],
      render:((row: DSTStatement)=>row.creationSource),
      searchProp:'creationSource'
    }
  ]
  colFiltersChanged: boolean;
  markAsOptions!: DSTMarkAsOption[];
  markAsReason: string;
  activeRow!: DSTStatement;
  userClickedRow!: DSTStatement;
  scrollBarWidth: number;
  alert: Alert;
  showConfirmDeleteModal: boolean = false;
  showMarkOtherReasonModal: boolean = false;
  showConfirmUnMatchModal: boolean = false;
  showProjectTaggingModal: boolean = false;
  showMatchTrackerModal: boolean = false;
  showCopyRepModal: boolean = false;
  showOptionsModal: boolean = false;
  copyMode!: string;
  formOptions!: DSTFormOption[];
  showPDFViewerModal: boolean = false;
  stmtDetailsLoaded: boolean = false;
  dstProformaData: any;
  totalRecords: number = 0;
  exportingExcel: boolean = false;
  globalSearchTerm: string = '';

  private onDisSaveSubs!: Subscription;
  private onDisCancelSubs!: Subscription;
  private taxYear: string;
  selectedTaxYearItem: any = {};
  private selectedMes: string;
  ddRow!: DSTStatement;
  menuPos!: MenuPosPayload;

  private contextApplied = false;

  constructor(public dstSharedService: DSTSharedService,
              private sharedService: CreateDisclosureSharedService,
              private statementTrackerService: StatementTrackerService,
              private trackerTabsService: TrackerTabsService,
              private dCRService: DSTDynamicComponentResolverService,
              private stmtIdBtnService: StmtIdBtnService,
              private jsonExportService: JSONExportService,
              private changeDetectorRef: ChangeDetectorRef,
              private messageService: MessageService,
              private gtwGlobal: GlobalService,
              private gtwWorkspace: WorkspaceService,
              private gtwApp: AppService,
              private route: ActivatedRoute) {
    const initialTaxYearItem = this.taxYears.find((item: any) => item.selected) || this.taxYears[0];
    this.selectedTaxYearItem = initialTaxYearItem || {};
    this.taxYear = initialTaxYearItem?.label || '';
    this.alert = dstSharedService.alert;
    this.selectedMes = '';
    this.tabs = this.trackerTabsService.trackerTabs;
    this.tableData = [];
    this.tableDataFiltered = [];
    this.displayedColumns = [
      ...this.frozenCols.map((c) => c.field),
      ...this.cols.map((c) => c.field),
    ];
    this.filterColumns = this.displayedColumns.map((c) => `${c}_filter`);
    this.dataSource.globalFilterFields = [
      'statementId', 'leId', 'entityName', 'fiscalEnd', 'TAXYEAR', 'businessName',
      'disclosureShortName', 'creationSource', 'gtw_source_desc', 'associatedStatements',
      'projectName', 'projectStepName', 'PROJECTBSLACODE', 'dcnName', 'createdBy',
      'createdOnFormatted', 'modifiedBy', 'modifiedFormatted',
    ];
    this.table = new DstMatTableFilterApi(this.dataSource);
    this.table.onFilter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: DstFilterEvent<DSTStatement>) => {
      this.handleFilter(event);
    });
    this.colFiltersChanged = true;
    this.tableActionStatus = dstSharedService.tableActionStatus;
    this.filtersOptions = [];
    this.markAsReason = '';
    this.scrollBarWidth = 0;
    this.onDisSaveSubs = this.dCRService.onDisclosureSave.subscribe((payload: DSTSavePayload) => {
      this.statementTrackerService.refresh = true;
    });
    this.onDisCancelSubs = this.dCRService.onDisclosureCancel.subscribe(() => {
      const activeTab: TrackerTabState | null = this.trackerTabsService.getActiveTrackerTab();
      if (activeTab) {
        this.closeTab(activeTab.id);
      } else {
        // Tab was already removed by another component instance subscribed to the
        // same singleton Subject. Sync local state with the service so the view
        // re-renders (grid container shows when activeTab is falsy).
        this.activeTab = null;
        this.userClickedRow = undefined as any;
        if (this.statementTrackerService.refresh) {
          this.refresh();
        }
      }
    });
  }

  ngOnInit(): void {
    this.messageService.subscribe(StatementTrackerService.ID_CREATE_NEW,"show-data-loss-confirmation",(type)=>{
      this.showDataLossConfirmation.emit(type);
    });

    this.messageService.subscribe(StatementTrackerService.ID_CREATE_NEW,"close-data-loss-confirmed",()=>{
          this.hardDeleteStatement(this.sharedService.getFormStateArray()[0].statementId);
    });
    this.messageService.subscribe(StatementTrackerService.ID_CREATE_NEW,"is-child-statement-edit-parent",(payload)=>{
      this.isChildEditing.emit(payload);
    });

    this.applyWorkspaceContext();

    this.onDisSaveSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.onDisSaveSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    this.connectMatSort(this.scrollMatSort);
    refreshDstMatStickyColumns(this.matTable);
    if (this.contextApplied && this.dfMeDropdownComponent) {
      this.dfMeDropdownComponent.loadMEsCustom(this.taxYear);
    }
  }

  private applyWorkspaceContext(): void {
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(
        user.system_access_level
          ?? user.user_type_key
          ?? user.access_level
          ?? this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level
          ?? 0
      );
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);

      const currentYear = new Date().getFullYear().toString();
      this.generateTaxYears(taxYear || currentYear, taxYear || currentYear);

      if (!this.contextApplied) {
        this.contextApplied = true;
        this.loadMarkAsOptions();
        this.loadGridFilters();
        this.dfMeDropdownComponent?.loadMEsCustom(this.taxYear);
        const pId = this.route.snapshot.paramMap.get('pId') || '';
        const cId = this.route.snapshot.paramMap.get('cId') || '';
        this.refresh(false, pId, cId);
      }

      this.changeDetectorRef.detectChanges();
    } catch (e) {
      this.dstSharedService.showAlert('error', 'Missing gtw input params, please contact system administrator for assistance!');
    }
  }

  private prepopulateStatement(selectedStatementId: string,selectedChildStatementId: string) {
    const statementRow: DSTStatement | undefined = this.tableData.find(row => row.statementId === selectedStatementId);

    if (statementRow) {
      this.activeRow = statementRow;
      this.showViewCreate(statementRow,selectedChildStatementId);
    } else {
      this.dstSharedService.showAlert('error', 'Could not find the requested statment: ' + selectedStatementId);
    }
  }

  ngOnDestroy(): void {
    this.onDisSaveSubs?.unsubscribe();
    this.onDisCancelSubs?.unsubscribe();
    this.messageService.unsubscribe(StatementTrackerService.ID_CREATE_NEW,"show-data-loss-confirmation");
    this.messageService.unsubscribe(StatementTrackerService.ID_CREATE_NEW,"close-data-loss-confirmed");
    this.messageService.unsubscribe(StatementTrackerService.ID_CREATE_NEW,"is-child-statement-edit-parent");
  }

  get idCreateNew(): string {
    return StatementTrackerService.ID_CREATE_NEW;
  }

  searchg($event: any): void {
    const query = $event?.queryStr ?? $event?.query ?? $event?.target?.value ?? this.globalSearchTerm ?? '';
    this.globalSearchTerm = query;
    this.table.filterGlobal(query, 'contains');
  }

  clearGlobalSearch(): void {
    this.globalSearchTerm = '';
    this.table.filterGlobal('', 'contains');
  }

  filterCol(event: Event, field: string, matchMode: string): void {
    this.colFiltersChanged = true;
    this.table.filter((event.target as HTMLInputElement).value, field, matchMode);
  }
  
  filterMultiSelectCol(selected: DSTGridFilterVal[], field: string, matchMode?: string): void {
    this.colFiltersChanged = true;
    this.table.filter((selected || []).map((i: DSTGridFilterVal) => i.val), field, matchMode || 'in');
  }
  
  filterLeidCol(selected: DSTGridFilterVal[], field: string, splitString: string): void {
    this.colFiltersChanged = true;
    this.table.filter((selected || []).map((i: DSTGridFilterVal) => (i.val).toString().substring(0, (i.val).toString().indexOf(splitString))), field, 'in');
  }

  handleFilter(event: DstFilterEvent<DSTStatement>): void {
    this.totalRecords = event.filteredValue?.length || (Object.keys(this.table.filters).length === 0 ? this.tableData.length : 0);
    this.tableDataFiltered = event.filteredValue || [];
    this.populateFilters(event.filteredValue);
  }

  populateFilters(tableData: DSTStatement[]){
    this.filters.forEach(filter=>{
      const existingLeidFilterobj = _.find(this.filtersOptions,['filterId',filter.filterId])
      
      const filterObj: DSTGridFilter = existingLeidFilterobj || filter;
  
      const searchProp = filter.searchProp as keyof DSTStatement;
      const filterLeids: DSTGridFilterVal[] = _.uniqBy(tableData || [], filter.searchProp)
        .filter((row) => row[searchProp])
        .map((row) => ({ val: filter.render(row) } as DSTGridFilterVal));
      filterObj.filterValues = filterLeids;
      this.dropdownOptions[filter.filterId] = filterLeids.map((v: DSTGridFilterVal) => (<DSTGridFilterOption>{
        ...v,
        showCheckbox: true,
        role: 'checkbox'
      }));
      
      if(!existingLeidFilterobj)
        this.filtersOptions.push(filterObj);

    })
  }

  resetColumnFilters(): void {
    this.cols.forEach((item: any) => {
      item.filterUserInput = null;
    });
    this.frozenCols.forEach((item: any) => {
      item.filterUserInput = null;
    });
    this.table.reset();
    this.totalRecords = this.tableData.length;
    this.tableDataFiltered = this.tableData;
    this.populateFilters(this.tableData);
  }

  connectMatSort(sort: MatSort | undefined): void {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  onMatSortChange(): void {
    this.connectMatSort(this.scrollMatSort);
    if (this.ddRow) {
      this.hideStmtIdButtonDropdown();
    }
  }

  getColStyle(col: PtableColConfigModel): Record<string, string> {
    const width = col.width || '100px';
    return { width, minWidth: width, maxWidth: width };
  }

  getColWidthPx(col: PtableColConfigModel): number {
    return this.parseWidthPx(col.width);
  }

  onColumnWidthChange(col: PtableColConfigModel, widthPx: number): void {
    const width = `${widthPx}px`;
    col.width = width;
    this.changeDetectorRef.detectChanges();
    refreshDstMatStickyColumns(this.matTable);
  }

  private parseWidthPx(width?: string): number {
    if (!width) {
      return 100;
    }
    const parsed = Number.parseInt(width, 10);
    return Number.isNaN(parsed) ? 100 : parsed;
  }

  private generateTaxYears(latest: string, current: string): void {
    this.taxYears = [];
    const latestYear: number = Number.parseInt(latest);
    const currentYear: number = Number.parseInt(current);
    for (let i = latestYear; i >= (latestYear - 6) ; i--) {
      if (i < 2016) {
        continue;
      }

      this.taxYears.push({
        value: i,
        label: i.toString(),
        selected: i === currentYear
      });
    }
    const selectedTaxYearItem = this.taxYears.find((item: any) => item.selected) || this.taxYears[0];
    this.selectedTaxYearItem = selectedTaxYearItem || {};
    this.taxYear = selectedTaxYearItem?.label || current;
  }

  showTracker(): void {
    this.trackerTabsService.activeId = StatementTrackerService.ID_TRACKER;
    this.activeTab = this.trackerTabsService.getActiveTrackerTab();
    this.statementTrackerService.refresh && this.refresh();
  }

  /**
   * Navigate to an existing tab specified by tabId, if none exists, create a new tab
   * @param tabId is statementId for existing statements or csStatementId for create requirements
   * @param formOption is the option user selected from create options modal
   * @param childFormStates is a list of DSTChildFormState objects in which each object represents a child form state
   */
  addTabOrNavigate(tabId: string, formOption?: DSTFormOption, childFormStates?: DSTChildFormState[],childStatementIdToNavigate: string = ''): void {
    if (this.tableDataLoading) {
      return;
    }

    let tab: TrackerTabState | null = this.trackerTabsService.findTrackerTabById(tabId);
    let activeChildIndex;
    // create a new tracker tab
    if (!tab) {
      tab = new TrackerTabState(tabId);
      if (tabId !== StatementTrackerService.ID_CREATE_NEW) {
        if (formOption) {
          tab.addFormState(this.userClickedRow.TAXYEAR,
            this.userClickedRow.taxYearId,
            1,
            this.userClickedRow.placeHolder ? '0' : this.userClickedRow.statementId,
            Number.parseInt(formOption.FORMID),
            formOption.FORMNAME,
            '',
            Number.parseInt(formOption.FORMTYPE),
            this.userClickedRow.cseId,
            false,
            !this.userClickedRow.placeHolder && this.userClickedRow.statementId !== '0',
            undefined, undefined, this.userClickedRow.csStatementId, 
            (this.userClickedRow.statementId === '0' && this.userClickedRow.codeSectionId === 24));
        } else {
          tab.addFormState(this.userClickedRow.TAXYEAR,
            this.userClickedRow.taxYearId,
            1,
            this.userClickedRow.placeHolder ? '0' : this.userClickedRow.statementId,
            this.userClickedRow.formId,
            this.userClickedRow.formName,
            this.userClickedRow.ATTFORMTYPEDESC!,
            this.userClickedRow.FORMTYPEID!,
            this.userClickedRow.cseId,
            false,
            !this.userClickedRow.placeHolder && this.userClickedRow.statementId !== '0',
            undefined, undefined, this.userClickedRow.csStatementId,
            (this.userClickedRow.statementId === '0' && this.userClickedRow.codeSectionId === 24));

          
          childFormStates?.forEach((child: DSTChildFormState,index) => {
            tab!.addFormState(child.TAXYEAR, child.TAXYEARID, child.RETURNPERIODID,
              child.STATEMENTID.toString(), child.FORMID, child.STATEMENTNAME, child.FORMTYPE,
              child.FORMTYPEID, child.CSEID, true, true);

              if(childStatementIdToNavigate === child.STATEMENTID.toString()){
                // this.trackerTabsService.activeId = childStatementIdToNavigate;
                activeChildIndex = index + 1;
              }
          });
        }
      }
      tab.isPlaceholderChecked = this.userClickedRow.placeHolder === 1;
      this.trackerTabsService.createTrackerTab(tab);
    }
    if (tabId === StatementTrackerService.ID_CREATE_NEW) { //reset isFiled prop when create new tab gets active
      this.dstSharedService.setIsFiled(false);
    } else {
      this.dstSharedService.setIsFiled((this.userClickedRow.IS_FILED!));
    }
    this.trackerTabsService.activeId = tabId;
    this.activeTab = this.trackerTabsService.getActiveTrackerTab();
    this.trackerTabsService.setActiveTrackerTabChildIndex(activeChildIndex || 0);
  }

  closeTab(tabId: string, forceClose: boolean = false): void {
    const activeFormState = this.getActiveFormStateSafe();
    const isDifferentFromActiveForm =
      !!activeFormState &&
      tabId !== activeFormState.statementId &&
      tabId !== activeFormState.formData?.parentstatementid?.toString();

    if(
         isDifferentFromActiveForm &&
         this.sharedService.getActiveIndex() > 0 &&
         !activeFormState?.isView &&
         !forceClose){
      //show confirmation popup
      this.messageService.broadcast(StatementTrackerService.ID_CREATE_NEW,"show-data-loss-confirmation","close");

      return;
    }

    this.trackerTabsService.removeTrackerTabById(tabId);
    this.activeTab = this.trackerTabsService.getActiveTrackerTab();
    if (this.activeTab?.id === StatementTrackerService.ID_CREATE_NEW) { //reset isFiled prop when create new tab gets active
      this.dstSharedService.setIsFiled(false);
    }
    if (tabId === StatementTrackerService.ID_CREATE_NEW) {
      this.createNewTabIsOpen = false;
    }
    if (!this.activeTab && this.statementTrackerService.refresh) {
      this.refresh();
    }
  }

  private getActiveFormStateSafe(): any | null {
    try {
      return this.sharedService.getActiveFormState();
    } catch (error) {
      return null;
    }
  }

  loadGridFilters(): void {
    // this.dstSharedService.getGridFilterOptions(32508).subscribe((filters: DSTGridFilter[]) => {
    //   console.log(filters);
    //   this.filtersOptions = filters;
    // }, (e: Error) => {
    //   console.log(e);
    //   this.dstSharedService.showAlert('error', e.message);
    // });
  }

  hideStmtIdButtonDropdown(): void {
    this.ddRow.showDropdown = false;
    this.ddRow.showExtendedMenu = false;
  }

  refresh(force: boolean = false,preloadStatementId: string = '',preloadChildStatementId: string = ''): void {
    if (!force && !this.colFiltersChanged && !this.statementTrackerService.refresh) {
      return;
    }
    this.ddRow && this.hideStmtIdButtonDropdown();
    const filtersObj = {
      parent_me_string: this.selectedMes,
      statementId: '',
      tax_year: this.taxYear,
      leId: '',
      disclosureShortName: '',
      creationSource: '',
      createdBy: '',
      projectName: ''
    };
    this.tableData = [];
    this.tableDataLoading = true;
    this.dstSharedService.getGridData(32523, filtersObj).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: DSTStatement[]) => {
        // The grid endpoint occasionally returns `null` (HTTP-200 with empty
        // body) when the filter set matches no statements. Coalesce to `[]`
        // so the subsequent `.forEach` / `.length` / `populateFilters` calls
        // can't throw "Cannot read properties of null (reading 'forEach')".
        // Mirrors the same defensive pattern used by
        // `statements-filed-status.component.ts` line 274.
        const rows: DSTStatement[] = data || [];
        rows.forEach((d: DSTStatement) => {
          if (d.is304) {
            d.formId = -192;
          }
        });
        this.tableData = rows;
        this.dataSource.data = rows;
        this.totalRecords = this.tableData.length;
        this.tableDataFiltered = rows;

        this.populateFilters(this.tableData);
      }, (e: Error) => {
        this.tableData = [];
        console.log(e);
        this.dstSharedService.showAlert('error', e.message, 999);
      }).add(() => {
      this.colFiltersChanged = false;
      this.tableDataLoading = false;
      this.statementTrackerService.refresh = false;

      this.connectMatSort(this.scrollMatSort);
      refreshDstMatStickyColumns(this.matTable);

      if(preloadStatementId)
        this.prepopulateStatement(preloadStatementId,preloadChildStatementId);
    });
  }

  onScroll() : void {
    this.ddRow && this.hideStmtIdButtonDropdown(); //Event to hide stmt id button dropdown on scroll
  }

  private leidFilterValsConverter(filterVals: DSTGridFilterVal[]): DSTGridFilterVal[] {
    const result: DSTGridFilterVal[] = [];
    filterVals?.forEach((filterVal: DSTGridFilterVal) => {
      result.push({val: (filterVal.val as string).split('-')[0].trim()});
    });
    return result;
  }

  getFilterOptionByID(filterId: string): DSTGridFilterVal[] | [] {
    return this.filtersOptions.find((f: DSTGridFilter) => f.filterId === filterId)?.filterValues || [];
  }

  getDropdownOptionsByID(filterId: string): DSTGridFilterOption[] {
    // Returning a shared empty array keeps the binding stable before the grid
    // has loaded, so the dropdown is not handed a new reference each cycle.
    return this.dropdownOptions[filterId] || this.noDropdownOptions;
  }

  onDropdownFilterResult(filterId: string, event: any): void {
    this.dropdownOptions[filterId] = event?.result || [];
  }

  exportToExcel(): void {
    this.exportingExcel = true;
    const headersMap: JSONExportHeader[] = [
      {
        header: 'Tax Year',
        field: 'TAXYEAR'
      }, {
        header: 'Source',
        field: 'creationSource'
      }, {
        header: 'Creation Status',
        field: 'creationStatus'
      }, {
        header: 'Statement Id',
        field: 'statementId'
      }, {
        header: 'Statement Name',
        field: 'formName'
      }, {
        header: 'Type',
        field: 'codeSectionName'
      },{
        header: 'File Name',
        field: 'attachment_name'
      },{
        header: 'Primary Business',
        field: 'businessName'
      }, {
        header: 'Primary LEID',
        field: 'leId'
      }, {
        header: 'Primary Name',
        field: 'entityName'
      }, {
        header: 'Secondary Business',
        field: 'SECONDARY_BUSINESS'
      }, {
        header: 'Secondary LEID',
        field: 'SECONDARY_LEID'
      }, {
        header: 'Secondary Name',
        field: 'SECONDARY_ENTITY'
      }, {
        header: 'Filing Group',
        field: 'dcnName'
      }, {
        header: 'Project',
        field: 'projectName'
      }, {
        header: 'Project Steps',
        field: 'projectStepName'
      }, {
        header: 'Project BSLA',
        field: 'PROJECTBSLACODE'
      }, {
        header: 'Created By',
        field: 'createdBy'
      }, {
        header: 'Created On',
        field: 'createdOnFormatted'
      }, {
        header: 'Modified By',
        field: 'modifiedBy'
      }, {
        header: 'Modified On',
        field: 'modifiedFormatted'
      }, {
        header: 'Marked as Reviewed',
        field: 'isReviewedVal'
      }, {
        header: 'Reviewed By',
        field: 'reviewedBy'
      }, {
        header: 'Reviewed On',
        field: 'reviewedOn'
      }, {
        header: 'Election',
        field: 'ELECTIONFLAG'
      }
    ];
    const fileName: string = 'DST_StatementTracker_' + this.dstSharedService.getExcelExportDateString();

    const filtersObj = {
      parent_me_string: this.selectedMes,
      statementId: this.frozenCols[0].filterUserInput || '',
      tax_year: this.dstSharedService.getMultiSelectFilterInputAsString((this.cols[0].filterUserInput as DSTGridFilterVal[]), 'val') || this.taxYear,
      leId: this.dstSharedService.getMultiSelectFilterInputAsString((this.frozenCols[1].filterUserInput as DSTGridFilterVal[]), 'val', this.leidFilterValsConverter),
      disclosureShortName: this.dstSharedService.getMultiSelectFilterInputAsString((this.cols[2].filterUserInput as DSTGridFilterVal[]), 'val'),
      creationSource: this.dstSharedService.getMultiSelectFilterInputAsString((this.cols[3].filterUserInput as DSTGridFilterVal[]), 'val'),
      createdBy: this.cols[8].filterUserInput || '',
      projectName: this.dstSharedService.getMultiSelectFilterInputAsString((this.cols[6].filterUserInput as DSTGridFilterVal[]), 'val')
    };
    this.statementTrackerService.getDstStmtExportData(filtersObj.parent_me_string, filtersObj.statementId.toString(), filtersObj.tax_year
      , filtersObj.leId, filtersObj.disclosureShortName, filtersObj.creationSource, filtersObj.createdBy.toString(), filtersObj.projectName).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any[]) => {
        if (data.length > 0) {
          this.jsonExportService.exportToExcel(fileName, data, headersMap);
        } else {
          this.dstSharedService.showAlert('warning', "No Data found to Export");
        }

      }, (e: Error) => {
        console.log(e);
        this.dstSharedService.showAlert('error', e.message, 999);
      }).add(() => {
        this.exportingExcel = false;
    });
  }

  onTaxYearChanged(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    this.selectedTaxYearItem = change || this.selectedTaxYearItem;
    this.taxYear = change?.label ?? change?.text;
    this.dfMeDropdownComponent.loadMEsCustom(this.taxYear);
  }

  onTaxYearSelectItem(event: any): void {
    const selected = event?.selected ?? null;
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    const label = item?.label || item?.text || item?.value || '';
    return `Tax Year option ${label}`;
  }

  selectedMeChanged(selected: string): void {
    this.selectedMes = selected;
  }

  loadMarkAsOptions(): void {
    this.statementTrackerService.loadMarkAsOptions().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: DSTMarkAsOption[]) => {
        this.markAsOptions = response;
      });
  }

  createDisclosureNew(): void {
    this.ddRow && this.hideStmtIdButtonDropdown();
    // Always reset Create New flow when toolbar button is clicked. Without this,
    // `dst-create-disclosure` can keep stale local UI state (selected form /
    // open template pane) from a previous visit because the container stays alive.
    this.trackerTabsService.removeTrackerTabById(StatementTrackerService.ID_CREATE_NEW);

    const tab = new TrackerTabState(StatementTrackerService.ID_CREATE_NEW);
    this.trackerTabsService.createTrackerTab(tab);
    this.dstSharedService.setIsFiled(false);
    this.trackerTabsService.setActiveTrackerTabChildIndex(0);
    this.trackerTabsService.activeId = StatementTrackerService.ID_CREATE_NEW;
    this.activeTab = this.trackerTabsService.getActiveTrackerTab();

    // Force re-create of `dst-create-disclosure` so its local state returns to
    // the initial "Create New" selector flow.
    this.createNewTabIsOpen = false;
    setTimeout(() => {
      this.createNewTabIsOpen = this.trackerTabsService.getActiveTrackerTab()?.id === StatementTrackerService.ID_CREATE_NEW;
      this.changeDetectorRef.detectChanges();
    });
  }

  onActionClicked(payload: StmtBtnActionPayload): void {
    switch (payload.name) {
      case 'show-dropdown': {
        this.ddRow = payload.data.row;
        this.menuPos = payload.data.menuPos;
        break;
      }
      case 'show-view-update': {
        this.activeRow = payload.data;
        this.showViewCreate(this.activeRow,'');
        break;
      }
      case 'toggle-review': {
        this.activeRow = payload.data;
        this.toggleReview(payload.data);
        break;
      }
      case 'tagging-project': {
        this.activeRow = payload.data;
        this.onTaggingProject();
        break;
      }
      case 'copy': {
        this.activeRow = payload.data;
        this.onCopyReplicate('Copy');
        break;
      }
      case 'replicate': {
        this.activeRow = payload.data;
        this.onCopyReplicate('Replicate');
        break;
      }
      case 'deactivate': {
        this.activeRow = payload.data;
        this.showDeleteConfirmDialog();
        this.activeRow.showDropdown = false;
        break;
      }
      case 'unmatch-tracker': {
        this.activeRow = payload.data;
        this.showUnmatchConfirmDialog();
        break;
      }
      case 'mark-other-reason': {
        this.activeRow = payload.data;
        this.showMarkOtherReasonDialog();
        break;
      }
      case 'match-tracker': {
        this.activeRow = payload.data;
        this.onMatchTracker();
        break;
      }
      case 'save-success': {
        this.refresh();
        break;
      }
      default:
        return;
    }
  }

  showViewCreate(row: DSTStatement,childStatementIdToNavigate: string): void {
    this.userClickedRow = row;
    this.dstSharedService.setIsFiled((this.activeRow.IS_FILED!));
    if (row.placeHolder || row.statementId === '0') {
      // create mode
      this.dstSharedService.setTableActionStatus(true);
      this.statementTrackerService.getCreateStatements(row.cseId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: DSTFormOption[]) => {
        this.formOptions = data;
        if (data.length < 2) {
          this.addTabOrNavigate(row.csStatementId.toString());
        } else {
          this.showCreateOptions();
        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.dstSharedService.showAlert('error', 'Something went wrong while getting the create statements, please try again!');
      }).add(() => {
        this.dstSharedService.setTableActionStatus(false);
      });
    } else {
      // view mode
      this.viewDisclosure(row,childStatementIdToNavigate);
    }
  }

  private viewDisclosure(row: DSTStatement,childStatementIdToNavigate:string) {
    if (row.associatedStatements) {
      this.dstSharedService.setTableActionStatus(true);
      this.stmtIdBtnService.getChildStatements(row.statementId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
        this.addTabOrNavigate(row.statementId, undefined, childFormStates,childStatementIdToNavigate);
      }, (eMsg: string) => {
        console.log(eMsg);
        this.dstSharedService.showAlert('error', eMsg);
      }).add(() => {
        this.dstSharedService.setTableActionStatus(false);
      });
    } else {
      this.addTabOrNavigate(row.statementId);
    }
  }

  openChildDisclosure(childDisclosure: string, parentRow: DSTStatement): void {
    const childStatmentId = childDisclosure.split('-')[0].trim();
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled((parentRow.IS_FILED!));
    this.statementTrackerService.getDstStmtData(childStatmentId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: DSTStatement[]) => {
      console.log(data);
      if (data && data[0]) {
        this.userClickedRow = data[0];
        this.addTabOrNavigate(childStatmentId);
      }
    }, (error: HttpErrorResponse) => {
      console.log(error);
      this.dstSharedService.showAlert('error', 'Something went wrong while getting the create statements, please try again!');
    }).add(() => {
      this.dstSharedService.setTableActionStatus(false);
    });
  }

  showDropdown(thisRow: DSTStatement): void {
    this.tableDataFiltered.forEach((row: DSTStatement) => {
      row.showDropdown = false;
      row.showExtendedMenu = false;
    });
    thisRow.showDropdown = true;
  }

  toggleReview(row: DSTStatement): void {
    row.showDropdown = false;
    row.isReviewedVal = row.isReviewedVal === 1 ? 0 : 1;
    this.dstSharedService.setTableActionStatus(true);
    this.stmtIdBtnService.setReviewed(row.statementId, row.isReviewedVal).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: SaveActionResponse) => {
        if (response.callSuccess == 1) {
          this.dstSharedService.showAlert('success', 'Status change saved successfully!');
        } else {
          this.dstSharedService.showAlert('error', 'Something went wrong while saving your changes, please try again!');
        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.dstSharedService.showAlert('error', error.message);
      })
      .add(() => {
        this.dstSharedService.setTableActionStatus(false);
      });
  }

  onTaggingProject(): void {
    this.showProjectTaggingModal = true;
    setTimeout(() => {
      this.projectTaggingComponent.showMore('project-tagging');
    });
  }

  afterProjectTaggingModalClosed(): void {
    this.showProjectTaggingModal = false;
    this.projectTaggingComponent.closeModalOk('project-tagging');
  }

  onCopyReplicate(mode: string): void {
    this.showCopyRepModal = true;
    this.copyMode = mode;
    setTimeout(() => {
      this.copyRepComponent.showMore('copy-replicate');
    });
  }

  afterCopyRepModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showCopyRepModal = false;
  }

  onMatchTracker(): void {
    this.showMatchTrackerModal = true;
    setTimeout(() => {
      this.matchTrackerComponent.showMore('match-tracker');
    });
  }

  afterMatchTrackerModalClosed(): void {
    this.showMatchTrackerModal = false;
  }

  // onActiveRowChanged(row: DSTStatement): void {
  //   this.activeRow = row;
  // }

  private showCreateOptions(): void {
    this.showOptionsModal = true;
    setTimeout(() => {
      this.createOptionsComponent.showMore('create-options');
    });
  }

  afterOptionsModalClosed(option?: DSTFormOption): void {
    if (option) {
      this.addTabOrNavigate(this.userClickedRow.csStatementId.toString(), option);
    }
    this.showOptionsModal = false;
  }

  showDeleteConfirmDialog(): void {
    this.showConfirmDeleteModal = true;
    setTimeout(() => {
      this.dstDeleteConfirmDialog.showModal('confirm-delete');
    });
  }

  onDSTDeleteConfirmDialogOK(): void {
    // remove local statement
    // this.tableDataFiltered.splice(this.tableDataFiltered.indexOf(this.activeRow), 1);
    // this.tableData.splice(this.tableData.indexOf(this.activeRow), 1);
    this.stmtIdBtnService.delete(this.activeRow).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((success: boolean) => {
      this.refresh(true);
    });
  }

  hardDeleteStatement(statementId: string): void{
    this.stmtIdBtnService.hardDeleteStatementWithChild(statementId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(suuccess=>{
      console.log("Deleted statement successfully");
      this.closeTab(this.activeTab!.id,true);
    },err=>{
      console.error("Failed to delete the statement",err);
    })
  }

  closeDSTDeleteConfirmDialog(id: string): void {
    this.dstDeleteConfirmDialog.closeModal(id);
    setTimeout(() => {
      this.showConfirmDeleteModal = false;
    });
  }

  closeDSTDeleteConfirmDialogOk(id: string): void {
    this.onDSTDeleteConfirmDialogOK();
    this.dstDeleteConfirmDialog.closeModal(id);
    setTimeout(() => {
      this.showConfirmDeleteModal = false;
    });
  }

  showMarkOtherReasonDialog(): void {
    this.markAsReason = '';
    this.showMarkOtherReasonModal = true;
    setTimeout(() => {
      this.dstMarkOtherReasonDialog.showModal('mark-other-reason');
    });
  }

  onDSTMarkOtherReasonDialogOK(): void {
    // mark as other reason - reason id is 4
    this.stmtIdBtnService.markAs(this.activeRow, {REASONID: 4, REASON: this.markAsReason, REASONDESC: ''});
  }

  closeDSTMarkOtherReasonDialog(id: string): void {
    this.dstMarkOtherReasonDialog.closeModal(id);
    setTimeout(() => {
      this.showMarkOtherReasonModal = false;
    });
  }

  closeDSTMarkOtherReasonDialogOk(id: string): void {
    this.onDSTMarkOtherReasonDialogOK();
    this.dstMarkOtherReasonDialog.closeModal(id);
    setTimeout(() => {
      this.showMarkOtherReasonModal = false;
    });
  }

  showUnmatchConfirmDialog(): void {
    this.showConfirmUnMatchModal = true;
    setTimeout(() => {
      this.dstUnmatchConfirmDialog.showModal('confirm-unmatch');
    });
  }

  onDSTUnmatchConfirmDialogOK(): void {
    this.stmtIdBtnService.unmatch(this.activeRow); // TODO
  }

  closeDSTUnmatchConfirmDialog(id: string): void {
    this.dstUnmatchConfirmDialog.closeModal(id);
    setTimeout(() => {
      this.showConfirmUnMatchModal = false;
    });
  }

  closeDSTUnmatchConfirmDialogOk(id: string): void {
    this.onDSTUnmatchConfirmDialogOK();
    this.dstUnmatchConfirmDialog.closeModal(id);
    setTimeout(() => {
      this.showConfirmUnMatchModal = false;
    });
  }

  showPDFView(id: string, statementId: string): void {
    this.showPDFViewerModal = true;
    this.getPDFProformaParams(statementId);
    setTimeout(() => {
      this.pdfViewerModal.showModal(id);
    });
  }

  closePDFViewerModalOk(id: string): void {
    this.pdfViewerModal.closeModalOk(id);
    setTimeout(() => {
      this.showPDFViewerModal = false;
    });
  }

  getPDFProformaParams(statementId: string): void {

    this.dstProformaData = {};
    this.stmtDetailsLoaded = false;
    this.statementTrackerService.getPDFProformaParams(statementId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: FormReivewPdf[]) => {
      if (data.length < 1) {
        this.dstSharedService.showAlert('warning', 'No PDF details found for this statement!');
      } else {
        this.dstProformaData = data[0];
        this.dstProformaData.FORM_SUB_CAT = 'DIFF_PDF'; //Default values
        this.dstProformaData.SRC_TYPE = 'DOM';
        this.dstProformaData.JOB_ID = '0';
      }
    }, (error: HttpErrorResponse) => {
      console.log(error);
      this.dstSharedService.showAlert('error', 'Something went wrong while getting statement details, please try again!');
    }).add(() => {
      this.stmtDetailsLoaded = true;
    });
  }

  onProjectTagging(): void {
    this.onActionClicked({name: 'tagging-project', data: this.ddRow});
  }

  onCopyOrReplicate(mode: string): void {
    this.onActionClicked({name: mode.toLowerCase(), data: this.ddRow});
  }

  onShowDeleteConfirm(): void {
    this.onActionClicked({name: 'deactivate', data: this.ddRow});
  }

  onShowUnmatchConfirm(): void {
    this.onActionClicked({name: 'unmatch-tracker', data: this.ddRow});
  }

  onMatchStatementTracker(): void {
    this.onActionClicked({name: 'match-tracker', data: this.ddRow});
  }

  onShowMarkOtherReason(): void {
    this.onActionClicked({name: 'mark-other-reason', data: this.ddRow});
  }

  onSaveSuccess(): void {
    this.onActionClicked({name: 'save-success', data: this.ddRow});
  }

}
